import { useState } from "react";
import { router } from "@inertiajs/react";
import { UploadDropzone, UploadVideoSlot, VideoPlayer, RemoteFetchPanel } from "@components/uploads";
import type { ProcessingStatus, UploadSlot, VideoPayload } from "~/lib/uploads";

// E2E harness for the Phase 4 upload frontend + the Phase 5 player. Not a
// product page — it mounts the generic UploadDropzone and records the
// placeholder slots it hands back, plus an in-app navigation link so a test can
// prove `router.visit` never interrupts an in-flight transfer (the queue widget
// lives above the page layer, so it survives this navigation).
//
// Enqueued slots render through <UploadVideoSlot>, which transitions one region
// in place: the uploading placeholder, then the player once the upload is
// ingested. The player-harness section below is a pure VISUAL STUB — it drives
// <VideoPlayer> through each processing_status / payload pair (spinner, still-
// improving, failed, ready, quality menu) without a live transcode.

const STUB_VIDEO: VideoPayload = {
  hls_master_url: "https://uploads.launch10.com/video/demo/master.m3u8",
  playable: true,
  poster_url: "https://uploads.launch10.com/video/demo/poster.jpg",
  duration_seconds: 132,
  original: { url: "https://uploads.launch10.com/video/demo/original.mp4", playable: true },
  renditions: [
    { preset: "1080p", url: "https://uploads.launch10.com/video/demo/1080p.mp4", width: 1920, height: 1080 },
    { preset: "720p", url: "https://uploads.launch10.com/video/demo/720p.mp4", width: 1280, height: 720 },
  ],
};

// Mid-transcode with nothing playable yet: no completed rendition, no master.
const STUB_VIDEO_PRE_PLAYABLE: VideoPayload = {
  hls_master_url: null,
  playable: false,
  poster_url: "https://uploads.launch10.com/video/demo/poster.jpg",
  duration_seconds: 132,
  original: { url: "https://uploads.launch10.com/video/demo/original.mp4", playable: false },
  renditions: [],
};

// Tier 1: the original is the only playable source yet — no rungs, no master.
// The player plays it and shows the "still processing" line.
const STUB_VIDEO_ORIGINAL_ONLY: VideoPayload = {
  hls_master_url: null,
  playable: true,
  poster_url: "https://uploads.launch10.com/video/demo/poster.jpg",
  duration_seconds: 132,
  original: { url: "https://uploads.launch10.com/video/demo/original.mp4", playable: true },
  renditions: [],
};

// Each harness state pins a (processing_status, payload) pair the player can
// be driven through: pure processing (spinner over poster), processing with a
// playable rendition (plays + "quality will improve" line), failed, ready.
const HARNESS_STATES: Record<string, { status: ProcessingStatus; video: VideoPayload }> = {
  processing: { status: "processing", video: STUB_VIDEO_PRE_PLAYABLE },
  "processing-original": { status: "processing", video: STUB_VIDEO_ORIGINAL_ONLY },
  "processing-playable": { status: "processing", video: STUB_VIDEO },
  failed: { status: "failed", video: STUB_VIDEO },
  ready: { status: "ready", video: STUB_VIDEO },
};

const HARNESS_STATE_NAMES = Object.keys(HARNESS_STATES);

export default function VideoUploadTest() {
  // Hold the enqueue-time slot handles; UploadVideoSlot tracks the live slot
  // from the store itself and latches the ingested upload, so it stays mounted
  // (and the player keeps playing) even after the queue widget's dismissal
  // purges the slot. Never gate this list on store presence.
  const [enqueued, setEnqueued] = useState<UploadSlot[]>([]);
  const [playerState, setPlayerState] = useState<string>("ready");

  return (
    <div className="mx-auto max-w-2xl p-8">
      <h1 className="mb-6 text-xl font-semibold text-base-600">Video upload harness</h1>

      <UploadDropzone
        accept="video/*"
        onEnqueued={(slot) => setEnqueued((prev) => [...prev, slot])}
      />

      <div className="mt-6 flex flex-col gap-4" data-testid="enqueued-slots">
        {enqueued.map((slot) => (
          <UploadVideoSlot key={slot.id} slot={slot} />
        ))}
      </div>

      <RemoteFetchPanel />

      <div className="mt-10 border-t border-neutral-200 pt-6" data-testid="player-harness">
        <p className="mb-3 text-xs uppercase tracking-wide text-neutral-400">
          Visual stub — drives the player states without a live transcode
        </p>
        <div className="mb-3 flex items-center gap-2">
          <span className="text-sm font-medium text-base-600">Player state:</span>
          {HARNESS_STATE_NAMES.map((state) => (
            <button
              key={state}
              type="button"
              data-testid={`player-status-${state}`}
              onClick={() => setPlayerState(state)}
              className={
                playerState === state
                  ? "rounded-md bg-primary-500 px-3 py-1 text-xs text-white"
                  : "rounded-md border border-neutral-300 px-3 py-1 text-xs text-base-500"
              }
            >
              {state}
            </button>
          ))}
        </div>

        <VideoPlayer
          video={HARNESS_STATES[playerState].video}
          processingStatus={HARNESS_STATES[playerState].status}
          filename="demo.mp4"
        />
      </div>

      <button
        type="button"
        data-testid="navigate-away"
        onClick={() => router.visit("/dashboard")}
        className="mt-8 rounded-lg border border-neutral-300 px-4 py-2 text-sm text-base-500"
      >
        Go to dashboard (in-app navigation)
      </button>
    </div>
  );
}
